import type { AudioSource } from "@-/com.npmjs.expo-audio";
import type { GraphemeMorphemeMnemonicsPanelMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { normalizeTypedMeaningAnswer } from "@-/dev.improve.haniverse.lib.gloss.answer-variants";

export type VoiceStudyCard = { answer: string; components: string[] };
export type VoiceStudySide = "front" | "back";

/**
 * What one turn says: an optional recorded clip (the card's pinyin) played
 * before the TTS `text`. `bargeIn: false` keeps every transcript captured while
 * this prompt plays from being acted on (used for help, which names commands).
 */
export type VoiceStudyPrompt = { bargeIn?: boolean; clip?: AudioSource | null; text: string };

/** The same label the decomposition chips show: mnemonic keyword, then back, then reading gloss. */
function componentLabel(component: VoiceStudyCardComponent): string {
  return (
    component.componentMnemonic?.trim() ||
    component.back?.trim() ||
    component.pinyin.gloss?.trim() ||
    ""
  );
}

type VoiceStudyCardComponent = GraphemeMorphemeMnemonicsPanelMorphemeSlice["components"][number];

/** Use the authored, ordered mnemonic relation, preserving repeated components. */
export function getVoiceStudyCard(
  card: GraphemeMorphemeMnemonicsPanelMorphemeSlice | null,
): VoiceStudyCard | null {
  const answer = card?.back?.trim();
  // Strokes and primitives have no components and nothing to spell out by ear.
  if (!card || !answer || card.components.length === 0) {
    return null;
  }
  const parts = card.components.map((component) => componentLabel(component));
  // A missing component name cannot be replaced with a glyph in an eyes-free prompt.
  if (parts.some((part) => part.length === 0)) {
    return null;
  }
  return { answer, components: parts };
}

/** Speak prose, not its Markdown links, `[bracketed]` keywords, formatting, or image URLs. */
export function spokenMarkdown(markdown: string): string {
  return markdown
    .replaceAll(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replaceAll(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replaceAll(/[*_`[\]]/g, "")
    .replaceAll(/^\s*(?:#+|>|[-+]\s)\s*/gm, "")
    .replaceAll(/\s+/g, " ")
    .trim();
}

const SENTENCE_END = /[.!?]$/;

function sentence(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return "";
  }
  return SENTENCE_END.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** `xian: stretch, stick, pi` — or just the components when the recorded clip says the pinyin. */
export function buildVoiceStudyFrontPrompt({
  card,
  clip,
  syllable,
}: {
  card: VoiceStudyCard;
  clip: AudioSource | null;
  syllable: string;
}): VoiceStudyPrompt {
  const components = card.components.join(", ");
  const spokenSyllable = clip === null ? syllable.trim() : "";
  return {
    clip,
    text: spokenSyllable.length > 0 ? `${spokenSyllable}: ${components}` : components,
  };
}

/**
 * `yuan: first. stack, son. <mnemonic>. mnemonic: New Year's Day. <description>` —
 * the pinyin is the recorded clip when one exists, otherwise spoken by TTS.
 */
export function buildVoiceStudyBackPrompt({
  card,
  clip,
  description = "",
  mnemonic = "",
  mnemonicTerm,
  syllable,
}: {
  card: VoiceStudyCard;
  clip: AudioSource | null;
  description: string | null | undefined;
  mnemonic: string | null | undefined;
  mnemonicTerm: string | null | undefined;
  syllable: string;
}): VoiceStudyPrompt {
  const spokenSyllable = clip === null ? syllable.trim() : "";
  const answer = spokenSyllable.length > 0 ? `${spokenSyllable}: ${card.answer}` : card.answer;
  const term = mnemonicTerm?.trim() ?? "";
  const parts = [
    sentence(answer),
    sentence(card.components.join(", ")),
    sentence(spokenMarkdown(mnemonic ?? "")),
    term.length > 0 ? sentence(`mnemonic: ${term}`) : "",
    sentence(spokenMarkdown(description ?? "")),
  ];
  return { clip, text: parts.filter((part) => part.length > 0).join(" ") };
}

export function buildVoiceStudyHelp(side: VoiceStudySide): VoiceStudyPrompt {
  const text =
    side === "front"
      ? "Say the meaning to answer. Say tap to reveal the answer, skip to defer this card, or repeat to hear it again. You can also say pause, stop, mute, unmute, lock, or unlock."
      : "Say next to rate this card, or again, hard, good, or easy. Say skip to defer it without rating, previous to go back to the front, or repeat to hear it again. You can also say pause, stop, mute, unmute, lock, or unlock.";
  // The help text names every command; never let its own echo trigger one.
  return { bargeIn: false, text };
}

export type VoiceStudyCommand = { text: string } & (
  | { kind: "rating"; rating: 1 | 2 | 3 | 4 }
  | {
      kind:
        | "answer"
        | "help"
        | "lock"
        | "mute"
        | "next"
        | "pause"
        | "repeat"
        | "reveal"
        | "skip"
        | "stop"
        | "unlock"
        | "unmute"
        | "unreveal";
    }
);

/** Whole-utterance commands only: “easy to remember” must never rate a card. */
export function parseVoiceStudyCommand(text: string): VoiceStudyCommand {
  const raw = text.trim();
  const normalized = normalizeTypedMeaningAnswer(text);
  switch (normalized) {
    case "again": {
      return { kind: "rating", rating: 1, text: raw };
    }
    case "hard": {
      return { kind: "rating", rating: 2, text: raw };
    }
    case "good": {
      return { kind: "rating", rating: 3, text: raw };
    }
    case "easy": {
      return { kind: "rating", rating: 4, text: raw };
    }
    case "tap":
    case "answer":
    case "reveal":
    case "show answer":
    case "show the answer": {
      return { kind: "reveal", text: raw };
    }
    case "next":
    case "next card": {
      return { kind: "next", text: raw };
    }
    case "skip":
    case "skip card":
    case "skip this card": {
      return { kind: "skip", text: raw };
    }
    case "previous":
    case "previous card":
    case "go back": {
      return { kind: "unreveal", text: raw };
    }
    case "help":
    case "commands": {
      return { kind: "help", text: raw };
    }
    case "repeat":
    case "repeat card": {
      return { kind: "repeat", text: raw };
    }
    case "pause": {
      return { kind: "pause", text: raw };
    }
    case "stop":
    case "stop voice":
    case "exit voice": {
      return { kind: "stop", text: raw };
    }
    case "mute": {
      return { kind: "mute", text: raw };
    }
    case "unmute": {
      return { kind: "unmute", text: raw };
    }
    case "lock":
    case "lock screen": {
      return { kind: "lock", text: raw };
    }
    case "unlock":
    case "unlock screen": {
      return { kind: "unlock", text: raw };
    }
    default: {
      return { kind: "answer", text: raw };
    }
  }
}

const BARGE_IN_KINDS = new Set<VoiceStudyCommand["kind"]>([
  "help",
  "next",
  "pause",
  "rating",
  "repeat",
  "skip",
  "stop",
]);

/** Commands that may interrupt a prompt mid-speech; anything else heard while speaking is echo. */
export function isVoiceStudyBargeInCommand(text: string): boolean {
  return BARGE_IN_KINDS.has(parseVoiceStudyCommand(text).kind);
}

/**
 * "Next" on the back rates with the preselection the answer left behind: a correct
 * spoken answer preset Easy; a tap/reveal (or an answer that never came) means Again.
 * Earlier wrong attempts are treated as misheard, not as failures.
 */
export function defaultVoiceStudyRating(presetRating: 1 | 2 | 3 | 4 | null): 1 | 4 {
  return presetRating === 4 ? 4 : 1;
}
